import type { ReactNode, MouseEvent } from 'react';
import { styled } from '@classify/react-core-components/styles';
import useClientNavigate from '../../hooks/useClientNavigate';
export interface Props {
  children: ReactNode;
  onClickHandlers?: Record<string, () => void>;
}
const Container = styled('div')(({
  theme: {
    palette,
    spacing,
    typography
  }
}) => ({
  '& a, & a:visited': {
    '&:not([class]), &[target="_blank"]': {
      ...typography.hyperlink,
      color: palette.interaction.enabled,
      '&:hover': {
        color: palette.interaction.hover,
        textDecoration: 'none'
      },
      fontWeight: 'normal',
      fontSize: 'inherit'
    }
  },
  '& a[target="_blank"]': {
    position: 'relative',
    display: 'inline-flex',
    '&::after': {
      content: 'url(data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZD0iTTE5IDE5SDVWNWg3VjNINWMtMS4xMSAwLTIgLjktMiAydjE0YzAgMS4xLjg5IDIgMiAyaDE0YzEuMSAwIDItLjkgMi0ydi03aC0ydjd6TTE0IDN2MmgzLjU5bC05LjgzIDkuODMgMS40MSAxLjQxTDE5IDYuNDFWMTBoMlYzaC03eiI+PC9wYXRoPjwvc3ZnPg==)',
      opacity: 0.6,
      position: 'relative',
      display: 'inline-block',
      left: spacing(0.5),
      marginRight: spacing(0.5),
      top: 4,
      width: 16,
      height: 16
    }
  }
}));
const ACTION_HASH_PREFIX = '#_action_';
const LinkHandler = ({
  children,
  onClickHandlers = {}
}: Props) => {
  let navigate: ReturnType<typeof useClientNavigate> | undefined;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigate = useClientNavigate();
  } catch {
    navigate = undefined;
  }
  const handleClickDelegation = (e: MouseEvent) => {
    const target = e.target as HTMLAnchorElement;

    // we only want to handle regular A tags with no classnames, because they "probably" come from our translations system
    if (target?.tagName !== 'A' || target?.classList.length > 0) {
      return;
    }
    const url = new URL((target as HTMLAnchorElement).href);
    const {
      hash
    } = url;
    if (hash && hash.startsWith(ACTION_HASH_PREFIX)) {
      const actionName = hash.replace(ACTION_HASH_PREFIX, '');
      if (typeof onClickHandlers[actionName] === 'function') {
        onClickHandlers[actionName]();
        e.preventDefault();
      }
      return;
    }
    if (url.origin === globalThis.location.origin && navigate) {
      const didNavigate = navigate(url.pathname + url.search + url.hash);
      if (didNavigate) {
        e.preventDefault();
      }
    }
  };

  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
  return <Container onClick={handleClickDelegation}>{children}</Container>;
};
export default LinkHandler;